import { useEffect, useMemo, useRef, useState } from "react";

import { cn } from "@/lib/utils";
import { v4 as uuidv4 } from "uuid";
import showdown from "showdown";
import DOMPurify from "dompurify";
import parse from "html-react-parser";

import Logo from "@/assets/logo.svg";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { AdminJobDetails } from "@/queries/admin/job-details";
import { useSendVenturiJobChatMessage } from "@/mutations/venturi/send-venturi-job-chat";
import { useVenturiJobChatQuery } from "@/queries/venturi/venturi-job-chat";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form, FormControl, FormField } from "@/components/ui/form";

const VenturiBotWidget = ({ job }: { job: AdminJobDetails }) => {
  const [chatId] = useState(uuidv4());
  const [chatOpen, setChatOpen] = useState(false);

  return (
    <>
      <button
        className={cn(
          "fixed bottom-5 right-5",
          "z-30 h-8 w-8 md:h-16 md:w-16 p-2 bg-[#4d9680] shadow-[0_4px_10px_rgba(0,0,0,0.1)] rounded-full cursor-pointer",
          "flex items-center justify-center overflow-hidden",
          "hover:scale-105 hover:shadow-[0_6px_12px_rgba(0,0,0,0.2)]",
          "transition-[transform,shadow] duration-300",
          chatOpen && "hidden",
        )}
        onClick={() => setChatOpen(true)}
      >
        <img className="h-full w-full md:h-8 md:w-8" src={Logo} />
      </button>
      <Chat
        jobId={job.id}
        chatId={chatId}
        chatOpen={chatOpen}
        setChatOpen={setChatOpen}
      />
    </>
  );
};

export default VenturiBotWidget;

const inputSchema = z.object({
  message: z.string({ required_error: "Note is required" }),
});

type InputSchema = z.infer<typeof inputSchema>;

const converter = new showdown.Converter({
  tables: true,
  strikethrough: true,
  tasklists: true,
  simpleLineBreaks: true,
});

const Chat = ({
  jobId,
  chatId,
  chatOpen,
  setChatOpen,
}: {
  jobId: string;
  chatId: string;
  chatOpen: boolean;
  setChatOpen: (v: boolean) => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const { venturiJobChat } = useVenturiJobChatQuery(chatId);
  const { sendVenturiJobChatMessage, isSendingVenturiJobChatMessage } =
    useSendVenturiJobChatMessage(chatId, jobId);

  // Initialize the chat on open
  useEffect(() => {
    if (
      chatOpen &&
      !venturiJobChat?.initialized &&
      !isSendingVenturiJobChatMessage
    ) {
      sendVenturiJobChatMessage(
        "Initial connection from URL: " + window.location.href,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatOpen]);

  // Convert messages from markdown to HTML
  const messages = useMemo(() => {
    if (!venturiJobChat) {
      return [];
    }

    return venturiJobChat.messages.map(({ from, message }, idx) => {
      const rawHtml = converter.makeHtml(message);
      const sanitizedHtml = DOMPurify.sanitize(rawHtml);
      const jsx = parse(sanitizedHtml);

      return (
        <div
          key={`venturi-chat-${idx}`}
          className={cn(
            "chat-message",
            from === "Bot" ? "chat-bot-message" : "chat-user-message",
          )}
        >
          {jsx}
        </div>
      );
    });
  }, [venturiJobChat]);

  useEffect(() => {
    ref.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Send a message
  const submitMessage = (message: string) => {
    sendVenturiJobChatMessage(message);
    form.reset();
  };

  const form = useForm<InputSchema>({
    resolver: zodResolver(inputSchema),
    defaultValues: {
      message: "",
    },
  });

  return (
    <>
      <div
        className={cn(
          "fixed bottom-5 right-5",
          "z-30 h-1/2 w-[90vw] max-h-[calc(100vh-120px)] max-w-screen-sm",
          "bg-white shadow-[0_4px_10px_rgba(0,0,0,0.1)] rounded-xl",
          "hidden flex-col overflow-hidden",
          "transition-all duration-300 ease-in-out",
          chatOpen && "flex",
        )}
      >
        <div
          className={cn(
            "p-3 md:p-5 flex items-center justify-between bg-[#2d5749]",
            "text-lg md:text-xl text-white font-bold",
          )}
        >
          <span>Venturi - Candidate Finder</span>
          <Button variant="ghostWithIcon" onClick={() => setChatOpen(false)}>
            <X />
          </Button>
        </div>
        <div className={cn("p-5 flex-1 overflow-y-auto scroll-smooth")}>
          {messages}
          {isSendingVenturiJobChatMessage && (
            <div className="chat-message chat-bot-message chat-typing-indicator">
              <span />
              <span />
              <span />
            </div>
          )}
          <div ref={ref} />
        </div>
        <div className="p-4 flex gap-2 border-t border-[#ddd]">
          <Form {...form}>
            <form
              className="w-full flex gap-2"
              onSubmit={(ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                void form.handleSubmit(({ message }) =>
                  submitMessage(message),
                )();
              }}
            >
              <div className="w-full">
                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Type your message here..."
                        className="flex-1"
                      />
                    </FormControl>
                  )}
                />
              </div>
              <Button
                variant="secondary"
                disabled={
                  !form.formState.isValid || !form.getValues("message").length
                }
              >
                Send
              </Button>
            </form>
          </Form>
        </div>
      </div>
    </>
  );
};
